import React, { useState } from 'react';
import {
    Box,
    Drawer,
    Typography,
    IconButton,
    Tabs,
    Tab,
    TextField,
    FormControlLabel,
    Switch,
    Select,
    MenuItem,
    RadioGroup,
    Radio,
    Button,
    useTheme,
    Divider,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    FormControl,
    InputLabel,
    Checkbox,
    Snackbar,
    Alert,
    CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { DPOptionOption, dpOptionsService, NewDPOption } from '../../services/dpoptions-service';
import { config } from '../../config/config';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel = (props: TabPanelProps) => {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`dp-option-tabpanel-${index}`}
            aria-labelledby={`dp-option-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
};


interface AddDPOptionPanelProps {
    open: boolean;
    onClose: () => void;
    onSave?: () => void;
}

const AddDPOptionPanel: React.FC<AddDPOptionPanelProps> = ({ open, onClose, onSave }) => {
    const theme = useTheme();
    const [currentTab, setCurrentTab] = useState(0);
    const [displayAs, setDisplayAs] = useState<'BUTTON' | 'CHECKBOX'>('BUTTON');
    const [options, setOptions] = useState<DPOptionOption[]>([{
        optionValue: '',
        optionDescription: '',
        isDefaultSelected: false,
        displayOrder: 0
    }]);
    const [allowMultiple, setAllowMultiple] = useState(false);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [isMandatory, setIsMandatory] = useState(false);
    const [nameError, setNameError] = useState(false);
    const [optionsError, setOptionsError] = useState(false);
    const [defaultSelectedOption, setDefaultSelectedOption] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setCurrentTab(newValue);
    };

    const resetForm = () => {
        setName('');
        setDescription('');
        setIsMandatory(false);
        setDisplayAs('BUTTON');
        setAllowMultiple(false);
        setOptions([{
            optionValue: '',
            optionDescription: '',
            isDefaultSelected: false,
            displayOrder: 0
        }]);
        setDefaultSelectedOption(null);
        setNameError(false);
        setOptionsError(false);
        setCurrentTab(0);
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const validateAndSave = async () => {
        let isValid = true;
        
        // Validate name (minimum 3 characters)
        if (!name.trim() || name.trim().length < 3) {
            setNameError(true);
            isValid = false;
            setCurrentTab(0);
        } else {
            setNameError(false);
        }

        // Validate options (all option values must be non-empty)
        const hasEmptyOptions = options.some(opt => !opt.optionValue.trim());
        if (hasEmptyOptions) {
            setOptionsError(true);
            isValid = false;
            if (name.trim().length >= 3) {
                setCurrentTab(1);
            }
        } else {
            setOptionsError(false);
        }

        if (!isValid) {
            return;
        }

        try {
            setSaving(true);
            const newDPOption: NewDPOption = {
                name: name.trim(),
                description: description.trim(),
                isMandatory,
                languageCode: 'en', // TODO: Get from language context
                displayAs,
                allowMultipleSelection: displayAs === 'CHECKBOX' ? allowMultiple : false,
                setADefaultSelection: defaultSelectedOption !== null || options.some(opt => opt.isDefaultSelected),
                options: options.map((opt, index) => ({
                    optionValue: opt.optionValue.trim(),
                    optionDescription: opt.optionDescription?.trim(),
                    isDefaultSelected: opt.isDefaultSelected,
                    displayOrder: index
                }))
            };

            await dpOptionsService.createDPOption(newDPOption);
            resetForm();
            onClose();
            onSave?.();
        } catch (error: any) {
            console.error('Server error response:', error.response?.data || error);
            setError(error.response?.data?.message || 'Failed to create preference');
        } finally {
            setSaving(false);
        }
    };

    const handleAddOption = () => {
        setOptions([...options, {
            optionValue: '',
            optionDescription: '',
            isDefaultSelected: false,
            displayOrder: options.length
        }]);
    };

    const handleRemoveOption = (index: number) => {
        setOptions(options.filter((_, i) => i !== index));
        if (defaultSelectedOption === index) {
            setDefaultSelectedOption(null);
        }
    };

    const handleOptionChange = (index: number, field: keyof DPOptionOption, value: string | boolean) => {
        const newOptions = [...options];
        newOptions[index] = {
            ...newOptions[index],
            [field]: value
        };
        setOptions(newOptions);
    };

    const handleDefaultSelectionChange = (index: number) => {
        if (allowMultiple) {
            // For multiple selection, toggle the checkbox
            handleOptionChange(index, 'isDefaultSelected', !options[index].isDefaultSelected);
        } else {
            // For single selection, update radio selection
            setDefaultSelectedOption(index);
            const newOptions = options.map((opt, i) => ({
                ...opt,
                isDefaultSelected: i === index
            }));
            setOptions(newOptions);
        }
    };

    const handleMoveOption = (index: number, direction: 'up' | 'down') => {
        const newOptions = [...options];
        if (direction === 'up' && index > 0) {
            [newOptions[index], newOptions[index - 1]] = [newOptions[index - 1], newOptions[index]];
            newOptions[index].displayOrder = index;
            newOptions[index - 1].displayOrder = index - 1;
        } else if (direction === 'down' && index < options.length - 1) {
            [newOptions[index], newOptions[index + 1]] = [newOptions[index + 1], newOptions[index]];
            newOptions[index].displayOrder = index;
            newOptions[index + 1].displayOrder = index + 1;
        }
        setOptions(newOptions);
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={handleClose}
            PaperProps={{
                sx: {
                    width: '600px',
                    bgcolor: theme.palette.background.paper,
                    borderLeft: `1px solid ${theme.palette.divider}`,
                },
            }}
        >
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* Header */}
                <Box sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}>
                    <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                        Create Preference
                    </Typography>
                    <IconButton onClick={handleClose} sx={{ color: theme.palette.text.primary }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Tabs */}
                <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <Tabs
                        value={currentTab}
                        onChange={handleTabChange}
                        sx={{
                            '& .MuiTab-root': {
                                color: theme.palette.text.secondary,
                                '&.Mui-selected': {
                                    color: theme.palette.primary.main,
                                },
                            },
                        }}
                    >
                        <Tab label="Details" />
                        <Tab label="Choices" />
                    </Tabs>
                </Box>

                {/* Tab Panels */}
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                    <TabPanel value={currentTab} index={0}>
                        <Stack spacing={3}>
                            <TextField
                                label="Name"
                                fullWidth
                                variant="outlined"
                                size="small"
                                required
                                value={name}
                                onChange={e => {
                                    setName(e.target.value);
                                    setNameError(false);
                                }}
                                error={nameError}
                                helperText={nameError ? "Name must be at least 3 characters long" : ""}
                            />

                            <TextField
                                label="Description"
                                fullWidth
                                multiline
                                rows={4}
                                variant="outlined"
                                size="small"
                            />

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={isMandatory}
                                        onChange={(e) => setIsMandatory(e.target.checked)}
                                    />
                                }
                                label="Mark this preference as mandatory"
                            />
                        </Stack>
                    </TabPanel>

                    <TabPanel value={currentTab} index={1}>
                        <Stack spacing={3}>
                            {name && (
                                <Typography variant="subtitle1" sx={{ color: theme.palette.text.primary }}>
                                    Add options to: {name}
                                </Typography>
                            )}

                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1, color: theme.palette.text.secondary }}>
                                    Display as
                                </Typography>
                                <RadioGroup
                                    value={displayAs}
                                    onChange={(e) => setDisplayAs(e.target.value as 'BUTTON' | 'CHECKBOX')}
                                    row
                                >
                                    <FormControlLabel value="BUTTON" control={<Radio />} label="Buttons" />
                                    <FormControlLabel value="CHECKBOX" control={<Radio />} label="Checkboxes" />
                                </RadioGroup>
                            </Box>

                            {displayAs === 'CHECKBOX' && (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={allowMultiple}
                                            onChange={(e) => setAllowMultiple(e.target.checked)}
                                        />
                                    }
                                    label="Allow multiple selections"
                                />
                            )}

                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ width: '40px' }}></TableCell>
                                            <TableCell sx={{ width: '35%' }}>Option Value</TableCell>
                                            <TableCell sx={{ width: '40%' }}>Description</TableCell>
                                            <TableCell align="center" sx={{ width: '80px' }}>Default</TableCell>
                                            <TableCell sx={{ width: '40px' }}></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {options.map((option, index) => (
                                            <TableRow key={index}>
                                                <TableCell>
                                                    <Stack direction="row" spacing={0}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleMoveOption(index, 'up')}
                                                            disabled={index === 0}
                                                            sx={{ 
                                                                color: theme.palette.text.secondary,
                                                                padding: '2px',
                                                                minWidth: '20px',
                                                                width: '20px',
                                                                height: '20px'
                                                            }}
                                                        >
                                                            <KeyboardArrowUpIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleMoveOption(index, 'down')}
                                                            disabled={index === options.length - 1}
                                                            sx={{ 
                                                                color: theme.palette.text.secondary,
                                                                padding: '2px',
                                                                minWidth: '20px',
                                                                width: '20px',
                                                                height: '20px'
                                                            }}
                                                        >
                                                            <KeyboardArrowDownIcon fontSize="small" />
                                                        </IconButton>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        placeholder="Enter option value"
                                                        required
                                                        value={option.optionValue}
                                                        onChange={(e) => {
                                                            handleOptionChange(index, 'optionValue', e.target.value);
                                                            setOptionsError(false);
                                                        }}
                                                        error={optionsError && option.optionValue.trim() === ''}
                                                        helperText={optionsError && option.optionValue.trim() === '' ? "Option value is required" : ""}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        placeholder="Enter description"
                                                        value={option.optionDescription || ''}
                                                        onChange={(e) => handleOptionChange(index, 'optionDescription', e.target.value)}
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    {displayAs === 'CHECKBOX' && allowMultiple ? (
                                                        <Checkbox
                                                            checked={option.isDefaultSelected}
                                                            onChange={() => handleDefaultSelectionChange(index)}
                                                            size="small"
                                                        />
                                                    ) : (
                                                        <Radio
                                                            checked={defaultSelectedOption === index}
                                                            onChange={() => handleDefaultSelectionChange(index)}
                                                            size="small"
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {options.length > 1 && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleRemoveOption(index)}
                                                            sx={{ 
                                                                color: theme.palette.error.main,
                                                                padding: '2px',
                                                                minWidth: '20px',
                                                                width: '20px',
                                                                height: '20px'
                                                            }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Button
                                startIcon={<AddIcon />}
                                onClick={handleAddOption}
                                sx={{
                                    alignSelf: 'flex-start',
                                    color: theme.palette.primary.main,
                                }}
                            >
                                Add Option
                            </Button>
                        </Stack>
                    </TabPanel>
                </Box>

                {/* Footer */}
                <Box sx={{
                    p: 2,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 1,
                }}>
                    <Button
                        onClick={handleClose}
                        disabled={saving}
                        sx={{ color: theme.palette.text.primary }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={validateAndSave}
                        disabled={saving}
                        sx={{
                            bgcolor: theme.palette.primary.main,
                            color: theme.palette.primary.contrastText,
                            '&:hover': {
                                bgcolor: theme.palette.primary.dark,
                            },
                            minWidth: 100,
                            position: 'relative'
                        }}
                    >
                        {saving ? (
                            <CircularProgress
                                size={24}
                                sx={{
                                    color: theme.palette.primary.contrastText,
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    marginTop: '-12px',
                                    marginLeft: '-12px',
                                }}
                            />
                        ) : (
                            'Save'
                        )}
                    </Button>
                </Box>
            </Box>

            {/* Add Snackbar for error messages */}
            <Snackbar 
                open={!!error} 
                autoHideDuration={config.ui.snackbar.autoHideDuration}
                onClose={() => setError(null)}
                anchorOrigin={config.ui.snackbar.anchorOrigin}
            >
                <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>
        </Drawer>
    );
};

export default AddDPOptionPanel; 